import { useCallback, useEffect, useState } from "react";
import { getPushPublicKey, subscribePush, unsubscribePush } from "../services/push";

const SW_URL = "/push-sw.js";

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushStatus = "indisponivel" | "negado" | "desativado" | "ativo" | "carregando";

export function usePush() {
  const [status, setStatus] = useState<PushStatus>("carregando");
  const [registro, setRegistro] = useState<ServiceWorkerRegistration | null>(null);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setStatus("indisponivel");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("negado");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
      setRegistro(reg);
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? "ativo" : "desativado");
    } catch {
      setStatus("indisponivel");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const ativar = useCallback(async () => {
    if (status === "indisponivel") return false;
    if (Notification.permission !== "granted") {
      const p = await Notification.requestPermission();
      if (p !== "granted") {
        setStatus(p === "denied" ? "negado" : "desativado");
        return false;
      }
    }
    try {
      const reg = registro || (await navigator.serviceWorker.register(SW_URL, { scope: "/" }));
      const { publicKey, habilitado } = await getPushPublicKey();
      if (!habilitado || !publicKey) {
        // Sem VAPID configurado no servidor — só permite notification in-page
        setStatus("desativado");
        return false;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await subscribePush(sub);
      setStatus("ativo");
      return true;
    } catch (e) {
      console.error("Falha ao ativar push", e);
      return false;
    }
  }, [status, registro]);

  const desativar = useCallback(async () => {
    try {
      const reg = registro || (await navigator.serviceWorker.getRegistration("/"));
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint);
        await sub.unsubscribe();
      }
      setStatus("desativado");
    } catch (e) {
      console.error("Falha ao desativar push", e);
    }
  }, [registro]);

  return { status, ativar, desativar, refresh };
}
